import React from "react";
import { Helmet } from "react-helmet";
import PageHero from "@webcore/components/domain/content-site/sections/PageHero";
import LogosStrip from "@webcore/components/domain/content-site/sections/LogosStrip";
import ThesisSection from "@webcore/components/domain/content-site/sections/ThesisSection";
import PrinciplesSection, { Principle } from "@webcore/components/domain/content-site/sections/PrinciplesSection";
import SplitSection from "@webcore/components/domain/content-site/sections/SplitSection";
import CardsSection, { CardData } from "@webcore/components/domain/content-site/sections/CardsSection";
import CalloutSection from "@webcore/components/domain/content-site/sections/CalloutSection";
import OfferSection from "@webcore/components/domain/content-site/sections/OfferSection";
import CtaSection from "@webcore/components/domain/content-site/sections/CtaSection";
import FaqSection from "@webcore/components/domain/content-site/sections/FaqSection";
import { ecommerceFaqs } from "@/content/faqs";
import { intakeBreathingCite, intakeBreathingQuote } from "@/content/testimonials";

import dailyPnlImg from "@/assets/workflows/daily-pnl.jpg";
import ltvCohortsImg from "@/assets/workflows/ltv-cohorts.jpg";

const connectorLogos = [
  { label: "Shopify", style: "mono" as const },
  { label: "Amazon" },
  { label: "TikTok", style: "italic" as const },
  { label: "Google Ads" },
  { label: "NetSuite", style: "italic" as const },
  { label: "QuickBooks" },
  { label: "Stripe", style: "serif" as const },
];

const steps: Principle[] = [
  {
    num: "01",
    kicker: "connect",
    title: (
      <>
        We connect your systems. On <em>one 30-minute call.</em>
      </>
    ),
    body: (
      <>
        Using DataFabrIQ's <strong>pre-built connectors</strong>, we connect your business with you
        on the call: an API key here, a login there. Missing a connector? We import your historical
        files directly so your data flows on day one, and our team builds the new connector in
        about a week. <strong>You build nothing.</strong>
      </>
    ),
  },
  {
    num: "02",
    kicker: "rebuild",
    title: (
      <>
        We rebuild your <em>one big spreadsheet.</em>
      </>
    ),
    body: (
      <>
        Show us your spreadsheet. Tell us how you define a new customer, a return, a cohort.{" "}
        <strong>Two weeks later, we hand it back as a live application</strong>: every tab, every
        roll-up, running on its own. The rules you defined live in a plain-English file we keep
        current, so when the business changes, the logic changes with it.
      </>
    ),
  },
  {
    num: "03",
    kicker: "run",
    title: (
      <>
        You run the business in less time, on numbers <em>you can audit.</em>
      </>
    ),
    body: (
      <>
        The daily P&amp;L that took hours to assemble now updates itself: revenue, spend, and
        contribution by channel, by day. When a number looks off, <strong>click it</strong> —
        you'll see the individual orders behind it. No forensic audit required.
      </>
    ),
  },
  {
    num: "04",
    kicker: "extend",
    title: (
      <>
        Then we <em>keep building.</em>
      </>
    ),
    body: (
      <>
        Under your spreadsheet is a blank whiteboard. Tell us how you want to run the business:
        LTV cohorts Amazon won't show you, selling price by channel, subscription retention by
        product group. <strong>If you can describe it, we can build it</strong> as a
        self-updating, verifiable dashboard, report, or process.
      </>
    ),
  },
];

const objections: CardData[] = [
  {
    title: (
      <>
        …buy <em>another dashboard?</em>
      </>
    ),
    body: (
      <>
        Northbeam and Triple Whale are good at what they're for: grading your ads. They tell you
        where the click came from. They can't tell you whether the order made money, because that
        answer lives in your finance and operations systems, and those aren't systems they connect
        to. <strong>DataFabrIQ starts with all of it</strong>: ads, orders, and actuals.
      </>
    ),
  },
  {
    title: (
      <>
        …build it on <em>a BI stack?</em>
      </>
    ),
    body: (
      <>
        Fivetran, BigQuery, Looker. They work… if someone maps every field from every system by
        hand and rebuilds the pipeline every time an API changes. It's configurable, so long as
        you have all the time in the world to configure it. With DataFabrIQ,{" "}
        <strong>the mapping is done by AI, verified by our team</strong>, and maintaining it is
        our job.
      </>
    ),
  },
  {
    title: (
      <>
        …just <em>use Claude?</em>
      </>
    ),
    body: (
      <>
        You could. Smart operators try. What they end up with is fragile: hard to secure, hard to
        share, and held together by whoever built it. It works right up until it doesn't.{" "}
        <strong>The layer everything else runs on is the last place to cut corners.</strong>
      </>
    ),
  },
];

// Proof section is on hold until the customer quote and figure are signed off — restore the
// section below (and this data) once they land.
/*
const proofPoints: CardData[] = [
  {
    title: "Three days to kill a daily spreadsheet.",
    body: (
      <>
        An ecommerce brand was keying its daily P&amp;L into a spreadsheet by hand, every morning,
        across three sales channels. <strong>We rebuilt it as a live application in three
        days.</strong> That page alone is what made them a customer.
      </>
    ),
    footnote: "[ pending — customer sign-off + a real figure ]",
  },
  {
    title: "In their words.",
    body: <em>"…"</em>,
    footnote: "[ testimonial — quote from the same ecommerce client, pending sign-off ]",
  },
];
*/

const SolutionsEcommerce: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>DataFabrIQ for Ecommerce — Your One Big Spreadsheet, Rebuilt as a Self-Updating App</title>
        <meta
          name="description"
          content="DataFabrIQ rebuilds the spreadsheet that runs your ecommerce business as a self-updating app — daily P&L, LTV cohorts, and channel margins across Shopify, Amazon, TikTok, and your ad platforms, auditable to the individual order. Built on your own data, free, before you pay anything."
        />
        <link rel="canonical" href="https://datafabriq.co/solutions/ecommerce" />
      </Helmet>

      <PageHero
        eyebrow="// solutions — ecommerce"
        heading={<>Have that one big spreadsheet that runs the whole business?</>}
        subheading={
          <>
            Fifteen tabs. Ad spend, channel sales, margins, LTV by cohort. Tied together by hand,
            every single week.
          </>
        }
        body={
          <p>
            <strong>DataFabrIQ rebuilds that spreadsheet as a self-updating app</strong> — accurate
            across every channel, and auditable down to the individual order.
          </p>
        }
        offer={
          <>
            Here's the deal: we build yours on your own data, free. You pay nothing until you've
            seen it work.
          </>
        }
        visual={<img src={dailyPnlImg} alt="Daily P&L by channel — the spreadsheet, reborn as a live application" />}
        primaryLabel="Book a Demo →"
        secondaryLabel="See how it works"
        secondaryHref="#how-it-works"
      />

      <CalloutSection quote={intakeBreathingQuote} cite={intakeBreathingCite} variant="testimonial" />

      <LogosStrip label="Seamlessly syncs ↗" entries={connectorLogos} />

      <ThesisSection
        id="who-its-for"
        label="// who it's for"
        heading={
          <>
            None of your systems talk to each other natively.<br />
            <span className="accent">Somebody has to marry them by hand.</span>
          </>
        }
        body={
          <>
            <p>
              <strong>Meta, TikTok, and Google Ads</strong> drive the demand. You sell on{" "}
              <strong>Shopify and Amazon</strong>. <strong>QuickBooks or NetSuite</strong> holds
              the financial truth. Together they hold the answer to what you should spend
              tomorrow, what to restock, and what channel made money yesterday.
            </p>
            <p>But every one reports its own numbers, in its own format, on its own schedule.</p>
            <p>
              So the founder begs the ads person for TikTok numbers. The CFO rebuilds a daily
              P&amp;L that's stale before it's finished. Cohorts won't match across channels no
              matter how many VLOOKUPs get thrown at them.
            </p>
            <p>
              Running the business requires numbers you can trust — and right now, producing those
              numbers requires time, effort, and pain.{" "}
              <strong>
                DataFabrIQ is for ecommerce operators who need a data team's output without hiring
                a data team.
              </strong>
            </p>
          </>
        }
      />

      <PrinciplesSection
        id="how-it-works"
        eyebrow="// how it works"
        heading={
          <>
            Done <span className="serif">for you,</span> not by you.
          </>
        }
        intro={
          <>
            One call to connect your systems. Two weeks to your rebuilt spreadsheet, live on your
            own data. No engineers hired, nothing for you to build — and we keep building from
            there.
          </>
        }
        principles={steps}
      />

      <SplitSection
        id="trust"
        eyebrow="// why you can trust it"
        heading={
          <>
            Numbers are only useful if you <span className="serif">trust them.</span>
          </>
        }
        body={
          <>
            <p>
              Every figure DataFabrIQ shows you traces back to source records. Click a month in a
              cohort table: <strong>the individual buyer IDs behind it</strong>, straight from
              your Amazon data.
            </p>
            <p>
              Question any number and you'll see how it was calculated and which records produced
              it, in seconds. When your CFO checks the work, <strong>the work is all there.</strong>
            </p>
            <p>
              We also derive numbers that don't exist anywhere else, like full LTV cohorts from
              your Amazon Seller data. What is a new customer actually worth? If you're raising
              money or betting big on acquisition, <strong>this is the analysis they'll
              believe.</strong> Receipts attached.
            </p>
          </>
        }
        visual={
          <img
            src={ltvCohortsImg}
            alt="LTV cohort retention matrix with repeat-rate trends, drillable to the buyers behind any cell"
          />
        }
      />

      <CardsSection
        id="alternatives"
        eyebrow="// the alternatives"
        heading={<>Couldn't we just…?</>}
        columns={3}
        cards={objections}
      />

      {/* Proof section on hold pending customer quote + figure sign-off:
      <CardsSection
        id="proof"
        eyebrow="// proof"
        heading={
          <>
            What this looks like <span className="serif">in practice.</span>
          </>
        }
        columns={2}
        cards={proofPoints}
      />
      */}

      <OfferSection
        id="offer"
        heading={
          <>
            See it working <span className="serif">before you pay for anything.</span>
          </>
        }
        lead={
          <>
            Book a demo. If you like what you see, we'll connect your systems on one 30-minute
            call. Two weeks later, you'll see your own spreadsheet rebuilt, automated, and running
            on your live data. If it isn't obviously better than what you're doing today, walk
            away. Nothing owed.
          </>
        }
        footnote={
          <>
            And if it is? DataFabrIQ is <strong>$3,000/month</strong>. For the whole platform,
            built and maintained for&nbsp;you.
          </>
        }
      />

      <FaqSection
        id="faq"
        eyebrow="// faq"
        heading={<>Questions, <span className="serif">answered.</span></>}
        faqs={ecommerceFaqs}
        footer={
          <>
            Question we didn't answer — like "can you connect to&nbsp;X?"{" "}
            <a href="mailto:support@datafabriq.co">Ask us anything →</a>
          </>
        }
      />

      <CtaSection
        heading={
          <>
            Stop running your business on a <span className="serif">spreadsheet.</span>
          </>
        }
        body="Start running it on AI-powered mini apps with numbers you can trust."
        primaryLabel="Book a Demo →"
      />
    </>
  );
};

export default SolutionsEcommerce;
